import type { SupportCategory, SupportSubcategory, SupportSubcategoryQuestion } from './types';
import { SUPPORT_DEPARTMENTS } from './departments';
import { FINANCE_CATEGORIES, FINANCE_SUBCATEGORIES, FINANCE_QUESTIONS } from './d-finance';
import { HR_CATEGORIES, HR_SUBCATEGORIES, HR_QUESTIONS } from './d-hr';
import { MARKETING_CATEGORIES, MARKETING_SUBCATEGORIES, MARKETING_QUESTIONS } from './d-marketing';
import {
  PROPERTY_MANAGEMENT_CATEGORIES,
  PROPERTY_MANAGEMENT_SUBCATEGORIES,
  PROPERTY_MANAGEMENT_QUESTIONS,
} from './d-property-management';
import { IT_CATEGORIES, IT_SUBCATEGORIES, IT_QUESTIONS } from './d-it';
import {
  PRCR_WAREHOUSE_CATEGORIES,
  PRCR_WAREHOUSE_SUBCATEGORIES,
  PRCR_WAREHOUSE_QUESTIONS,
  PRCR_SHIRTS_POLOS_SUBCATEGORY_ID,
} from './d-prcr-warehouse';
import { PURCHASING_CATEGORIES, PURCHASING_SUBCATEGORIES, PURCHASING_QUESTIONS } from './d-purchasing';
import { OPERATIONS_CATEGORIES, OPERATIONS_SUBCATEGORIES, OPERATIONS_QUESTIONS } from './d-operations';
import { ARB_APP_CATEGORIES, ARB_APP_SUBCATEGORIES, ARB_APP_QUESTIONS } from './d-arb-app';
import {
  MERCHANDISE_CATEGORIES,
  MERCHANDISE_SUBCATEGORIES,
  MERCHANDISE_QUESTIONS,
  MERCH_POLO_TSHIRT_SUBCATEGORY_ID,
} from './d-merchandise';
import { MAINTENANCE_CATEGORIES, MAINTENANCE_SUBCATEGORIES, MAINTENANCE_QUESTIONS } from './d-maintenance';
import {
  NEW_CONSTRUCTION_CATEGORIES,
  NEW_CONSTRUCTION_SUBCATEGORIES,
  NEW_CONSTRUCTION_QUESTIONS,
} from './d-new-construction';
import { buildShirtsPayloadFromAnswers } from './shirt-payload';

export { isQuestionVisible } from './isQuestionVisible';

export type {
  SupportDepartment,
  SupportCategory,
  SupportSubcategory,
  SupportQuestionOption,
  SupportQuestionVisibleWhen,
  SupportSubcategoryQuestion,
  SupportQuestionAnswerItem,
} from './types';

export { SUPPORT_DEPARTMENTS, PRCR_SHIRTS_POLOS_SUBCATEGORY_ID };

export const SHIRT_SUBCATEGORY_PREFIXES: Record<string, string> = {
  [PRCR_SHIRTS_POLOS_SUBCATEGORY_ID]: 'prcr',
  [MERCH_POLO_TSHIRT_SUBCATEGORY_ID]: 'merch',
};

export { buildShirtsPayloadFromAnswers };

const SUPPORT_CATEGORIES: SupportCategory[] = [
  ...FINANCE_CATEGORIES,
  ...HR_CATEGORIES,
  ...MARKETING_CATEGORIES,
  ...PROPERTY_MANAGEMENT_CATEGORIES,
  ...IT_CATEGORIES,
  ...PRCR_WAREHOUSE_CATEGORIES,
  ...PURCHASING_CATEGORIES,
  ...OPERATIONS_CATEGORIES,
  ...ARB_APP_CATEGORIES,
  ...MERCHANDISE_CATEGORIES,
  ...MAINTENANCE_CATEGORIES,
  ...NEW_CONSTRUCTION_CATEGORIES,
];

const SUPPORT_SUBCATEGORIES: SupportSubcategory[] = [
  ...FINANCE_SUBCATEGORIES,
  ...HR_SUBCATEGORIES,
  ...MARKETING_SUBCATEGORIES,
  ...PROPERTY_MANAGEMENT_SUBCATEGORIES,
  ...IT_SUBCATEGORIES,
  ...PRCR_WAREHOUSE_SUBCATEGORIES,
  ...PURCHASING_SUBCATEGORIES,
  ...OPERATIONS_SUBCATEGORIES,
  ...ARB_APP_SUBCATEGORIES,
  ...MERCHANDISE_SUBCATEGORIES,
  ...MAINTENANCE_SUBCATEGORIES,
  ...NEW_CONSTRUCTION_SUBCATEGORIES,
];

const SUPPORT_SUBCATEGORY_QUESTIONS: SupportSubcategoryQuestion[] = [
  ...FINANCE_QUESTIONS,
  ...HR_QUESTIONS,
  ...MARKETING_QUESTIONS,
  ...PROPERTY_MANAGEMENT_QUESTIONS,
  ...IT_QUESTIONS,
  ...PRCR_WAREHOUSE_QUESTIONS,
  ...PURCHASING_QUESTIONS,
  ...OPERATIONS_QUESTIONS,
  ...ARB_APP_QUESTIONS,
  ...MERCHANDISE_QUESTIONS,
  ...MAINTENANCE_QUESTIONS,
  ...NEW_CONSTRUCTION_QUESTIONS,
];

export function getCategoriesByDepartment(departmentId: string): SupportCategory[] {
  return SUPPORT_CATEGORIES.filter((c) => c.departmentId === departmentId);
}

export function getSubcategoriesByCategory(categoryId: string): SupportSubcategory[] {
  return SUPPORT_SUBCATEGORIES.filter((s) => s.categoryId === categoryId);
}

export function getQuestionsBySubcategory(subcategoryId: string): SupportSubcategoryQuestion[] {
  return SUPPORT_SUBCATEGORY_QUESTIONS.filter((q) => q.subcategoryId === subcategoryId);
}

export function hasTaxonomyCategories(departmentId: string): boolean {
  return getCategoriesByDepartment(departmentId).length > 0;
}
